// PasskeySetupPrompt — one-time popup that invites the user to set up a
// passkey on the current device after a successful password sign-in.
//
// Design:
//   - Reads a sessionStorage flag set by the login page to know that a fresh
//     password-based sign-in just happened.
//   - Reads a localStorage flag to know whether this device has already been
//     "handled" (prompt shown, registration completed, or passkey used to
//     sign in). If handled, the prompt never appears again on this device.
//   - Blocked by a prop so callers can defer display while other gates
//     (ForcePasswordChange, TermsAcceptanceGate) are still visible.
//   - Reuses the same registration ceremony as PasskeyManager — calls
//     register/start then register/finish — so the credential is identical
//     to one created via Account Settings.

import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { startRegistration } from "@simplewebauthn/browser";
import { apiRequest, queryClient } from "@/lib/queryClient";
import {
  isPasskeyAlreadyOnDeviceError,
  isPasskeyCancelledError,
  rethrowPasskeyRegistrationError,
} from "@/lib/passkey-errors";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Fingerprint, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";

export const PASSKEY_HANDLED_KEY = "veras_passkey_handled_v1";
export const PASSKEY_PROMPT_PENDING_KEY = "veras_passkey_prompt_pending";

function isWebAuthnSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.PublicKeyCredential !== "undefined" &&
    typeof window.navigator?.credentials?.create === "function"
  );
}

export function markDeviceHandled(): void {
  try {
    localStorage.setItem(PASSKEY_HANDLED_KEY, "1");
  } catch {
    // Ignore storage failures (private mode, quota).
  }
}

export function isDeviceHandled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem(PASSKEY_HANDLED_KEY) === "1";
  } catch {
    return false;
  }
}

/** Call this before navigating away on a successful passkey login so the
 * prompt never appears on a device that already used a passkey. */
export function markPasskeyLoginDevice(): void {
  markDeviceHandled();
}

/** Call this before navigating away on a successful password login so the
 * prompt knows to appear on the destination page. */
export function markPasswordLoginPending(): void {
  try {
    sessionStorage.setItem(PASSKEY_PROMPT_PENDING_KEY, "1");
  } catch {
    // Ignore.
  }
}

interface PasskeySetupPromptProps {
  /**
   * When true the prompt will not open even if all other conditions are met.
   * Use this to hold the prompt back while other full-screen gates
   * (ForcePasswordChange, TermsAcceptanceGate) are still visible so the
   * dialogs don't stack.
   */
  blocked?: boolean;
}

type PromptState = "offer" | "registering" | "success" | "error";

export function PasskeySetupPrompt({ blocked = false }: PasskeySetupPromptProps) {
  // Read + clear the sessionStorage pending flag exactly once on first render.
  const [pendingShow] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      const v = sessionStorage.getItem(PASSKEY_PROMPT_PENDING_KEY) === "1";
      if (v) sessionStorage.removeItem(PASSKEY_PROMPT_PENDING_KEY);
      return v;
    } catch {
      return false;
    }
  });

  const [open, setOpen] = useState<boolean>(() => {
    if (!pendingShow) return false;
    if (!isWebAuthnSupported()) return false;
    if (isDeviceHandled()) return false;
    return true;
  });

  const [state, setState] = useState<PromptState>("offer");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const registrationInFlight = useRef(false);

  const registerMutation = useMutation({
    mutationFn: async () => {
      try {
        const startRes = await apiRequest("POST", "/api/auth/passkey/register/start");
        const options = await startRes.json();
        const challengeId: string | undefined = options._challengeId;
        const attResp = await startRegistration({ optionsJSON: options });
        const finishRes = await apiRequest("POST", "/api/auth/passkey/register/finish", {
          ...attResp,
          _challengeId: challengeId,
          label: "My passkey",
        });
        if (!finishRes.ok) {
          const body = await finishRes.json().catch(() => ({}));
          throw new Error((body as any).error || "Registration failed");
        }
        return finishRes.json();
      } catch (err) {
        rethrowPasskeyRegistrationError(err);
      }
    },
    onSuccess: () => {
      markDeviceHandled();
      queryClient.invalidateQueries({ queryKey: ["/api/auth/passkey"] });
      setState("success");
    },
    onError: (err: unknown) => {
      registrationInFlight.current = false;
      if (isPasskeyCancelledError(err)) {
        setState("offer");
      } else if (isPasskeyAlreadyOnDeviceError(err)) {
        markDeviceHandled();
        setState("success");
      } else {
        setErrorMsg("Something went wrong — you can set up a passkey later in Account Settings.");
        setState("error");
      }
    },
    onSettled: () => {
      registrationInFlight.current = false;
    },
  });

  const handleSetUp = () => {
    if (registrationInFlight.current || registerMutation.isPending) return;
    registrationInFlight.current = true;
    setState("registering");
    registerMutation.mutate();
  };

  const handleSkip = () => {
    markDeviceHandled();
    setOpen(false);
  };

  const handleClose = () => {
    if (state === "offer" || state === "error") {
      markDeviceHandled();
    }
    setOpen(false);
  };

  // Re-evaluate whether to open now that blocked may have changed.
  // Because useState initializes once, we drive the real open/close
  // decision through the `open` state which is set only if all conditions
  // were met at mount.  If `blocked` clears after mount and `pendingShow`
  // was true but WebAuthn wasn't supported or device was already handled,
  // nothing will open — which is correct.
  const isOpen = open && !blocked;

  if (!isOpen && state !== "success") return null;
  if (!open) return null;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
    >
      <DialogContent
        className="max-w-sm"
        data-testid="modal-passkey-setup-prompt"
        onInteractOutside={state === "registering" ? (e) => e.preventDefault() : undefined}
        onEscapeKeyDown={state === "registering" ? (e) => e.preventDefault() : undefined}
      >
        {state === "success" ? (
          <>
            <DialogHeader>
              <div className="flex flex-col items-center gap-3 text-center pt-2">
                <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="h-6 w-6 text-green-600" />
                </div>
                <DialogTitle data-testid="text-passkey-prompt-success-title">
                  Passkey added!
                </DialogTitle>
                <DialogDescription>
                  Next time you sign in you can use Face ID, fingerprint, or your device PIN — no password needed.
                </DialogDescription>
              </div>
            </DialogHeader>
            <DialogFooter className="pt-2">
              <Button
                className="w-full"
                onClick={() => setOpen(false)}
                data-testid="button-passkey-prompt-done"
              >
                Got it
              </Button>
            </DialogFooter>
          </>
        ) : state === "error" ? (
          <>
            <DialogHeader>
              <DialogTitle data-testid="text-passkey-prompt-error-title">
                Couldn't set up a passkey
              </DialogTitle>
              <DialogDescription>
                {errorMsg}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="button-passkey-prompt-error-close">
                Close
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-3 mb-1">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <Fingerprint className="h-5 w-5 text-primary" />
                </div>
                <DialogTitle data-testid="text-passkey-prompt-title">
                  Sign in faster next time
                </DialogTitle>
              </div>
              <DialogDescription className="leading-relaxed">
                Set up a passkey to sign in with Face ID, fingerprint, or your device PIN — no password required. Your password stays active as a backup.
              </DialogDescription>
            </DialogHeader>

            <div className="flex items-start gap-2.5 rounded-lg bg-muted/50 px-3 py-2.5 text-sm text-muted-foreground">
              <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
              <span>Passkeys are device-bound — you'll keep being asked to sign in on new devices.</span>
            </div>

            <DialogFooter className="flex-col gap-2 sm:flex-col">
              <Button
                className="w-full gap-2"
                onClick={handleSetUp}
                disabled={registerMutation.isPending}
                data-testid="button-passkey-prompt-setup"
              >
                {registerMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Waiting for device…
                  </>
                ) : (
                  <>
                    <Fingerprint className="h-4 w-4" />
                    Set up a passkey
                  </>
                )}
              </Button>
              <Button
                variant="ghost"
                className="w-full text-muted-foreground"
                onClick={handleSkip}
                disabled={registerMutation.isPending}
                data-testid="button-passkey-prompt-skip"
              >
                Skip for now
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
